import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import {
  ApiError,
  formatDate,
  getMartyrs,
  HOUSES,
  upsertMartyr,
  type MartyrRow,
} from '../lib/api'
import { Card, Empty, ErrorBox, Spinner, WarnBox } from '../components/bits'
import { Modal } from '../components/Modal'

/**
 * Our Martyrs — the memorial roll.
 *
 * The handoff lists this in the sidebar with a count of 38 and nothing else: no
 * data model, no endpoint, no acceptance criteria. What follows is built on
 * stated assumptions rather than left as a placeholder, but they are assumptions
 * and the banner says so, because entering real names against a guessed schema is
 * not something to do quietly.
 *
 * The assumptions, each of which has a reason:
 *
 *   - **KitNo is optional and there is no foreign key to `members`.** A martyr may
 *     have died long before this register existed, or may simply never have
 *     registered. A memorial that can only honour people with accounts is the
 *     wrong shape.
 *   - **The name is stored separately** from any member record, because the name
 *     on a memorial is usually the formal one, not the one someone signed up with.
 *   - **Readable by any signed-in Petarian, editable only by a Secretariat admin.**
 *     Whether the roll should also be public is a decision for the college.
 */
export default function Martyrs() {
  const [rows, setRows] = useState<MartyrRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [editing, setEditing] = useState<MartyrRow | 'new' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setRows((await getMartyrs()) ?? [])
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="stack">
      <div>
        <div className="eyebrow">Memorial</div>
        <h1 style={{ marginTop: 8 }}>Our Martyrs</h1>
        <p className="small" style={{ marginTop: 6 }}>
          Petarians who gave their lives in service.
        </p>
      </div>

      <WarnBox>
        <strong>Confirm the shape of this before entering real names.</strong> The
        handoff lists this section but does not specify it, so these fields are
        assumptions: KitNo is optional because a martyr may predate the register;
        the roll is readable by any signed-in Petarian and editable only by a
        Secretariat admin; and the count of 38 in the design suggests a source
        list exists somewhere that should be imported rather than retyped.
      </WarnBox>

      {error && <ErrorBox>{error}</ErrorBox>}

      {notice && (
        <Card style={{ borderColor: '#2f8fd4' }}>
          <div className="row">
            <span className="small">{notice}</span>
            <div className="spacer" />
            <button className="btn btn-secondary btn-sm" onClick={() => setNotice(null)}>
              Dismiss
            </button>
          </div>
        </Card>
      )}

      <div className="row">
        <span className="small muted">
          {loading ? 'Loading…' : `${rows.length} on the roll`}
        </span>
        <div className="spacer" />
        <button className="btn btn-primary" onClick={() => setEditing('new')}>
          Add an entry
        </button>
      </div>

      <Card style={{ padding: 'var(--s-5)' }}>
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty
            title="The roll is empty"
            body="Add entries individually, or import the college's existing list once its format is known."
          />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>House</th>
                <th>Passed out</th>
                <th>Fell</th>
                <th style={{ textAlign: 'right' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id}>
                  <td>
                    <strong style={{ color: 'var(--text)' }}>
                      {m.rank_title ? `${m.rank_title} ` : ''}
                      {m.full_name}
                    </strong>
                    <br />
                    <span className="small muted">
                      {m.kit_no ? `KitNo ${m.kit_no}` : 'No KitNo on record'}
                      {m.unit ? ` · ${m.unit}` : ''}
                    </span>
                    {m.citation && (
                      <>
                        <br />
                        <span className="small muted">
                          {m.citation.length > 130
                            ? `${m.citation.slice(0, 130)}…`
                            : m.citation}
                        </span>
                      </>
                    )}
                  </td>
                  <td>{m.house ?? <span className="muted">—</span>}</td>
                  <td>{m.passing_year ?? <span className="muted">—</span>}</td>
                  <td className="small">
                    {m.date_of_death ? formatDate(m.date_of_death) : '—'}
                    {m.place && (
                      <>
                        <br />
                        <span className="muted">{m.place}</span>
                      </>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setEditing(m)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {editing && (
        <MartyrModal
          row={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={(message) => {
            setEditing(null)
            setNotice(message)
            void load()
          }}
        />
      )}
    </div>
  )
}

function MartyrModal({
  row,
  onCancel,
  onSaved,
}: {
  row: MartyrRow | null
  onCancel: () => void
  onSaved: (message: string) => void
}) {
  const [fullName, setFullName] = useState(row?.full_name ?? '')
  const [rankTitle, setRankTitle] = useState(row?.rank_title ?? '')
  const [kitNo, setKitNo] = useState(row?.kit_no ?? '')
  const [house, setHouse] = useState(row?.house ?? '')
  const [passingYear, setPassingYear] = useState(
    row?.passing_year ? String(row.passing_year) : '',
  )
  const [unit, setUnit] = useState(row?.unit ?? '')
  const [dateOfDeath, setDateOfDeath] = useState(row?.date_of_death ?? '')
  const [place, setPlace] = useState(row?.place ?? '')
  const [citation, setCitation] = useState(row?.citation ?? '')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save(): Promise<void> {
    if (fullName.trim().length < 3) {
      setError('A full name is required.')
      return
    }
    const year = passingYear.trim()
    if (year !== '' && (Number(year) < 1957 || Number(year) > new Date().getFullYear())) {
      setError('The passing year must be between 1957 and this year.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      const result = await upsertMartyr(row?.id ?? null, {
        full_name: fullName.trim(),
        rank_title: rankTitle.trim(),
        kit_no: kitNo.trim(),
        house: house.trim(),
        passing_year: year,
        unit: unit.trim(),
        date_of_death: dateOfDeath.trim(),
        place: place.trim(),
        citation: citation.trim(),
      })
      onSaved(`${result.fullName} ${row ? 'updated' : 'added to the roll'}.`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={row ? `Edit ${row.full_name}` : 'Add to the memorial roll'}
      onClose={onCancel}
      wide
      footer={
        <>
          <div className="spacer" />
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-accent" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : row ? 'Save' : 'Add to the roll'}
          </button>
        </>
      }
    >
      <p className="small" style={{ marginTop: 0 }}>
        Only the name is required. A martyr may predate the register, so a KitNo is
        optional and no link to a member account is assumed.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 'var(--s-4)',
          marginTop: 'var(--s-5)',
        }}
      >
        <Field label="Rank or title">
          <input
            className="input"
            value={rankTitle}
            onChange={(e) => setRankTitle(e.target.value)}
            placeholder="e.g. Major"
          />
        </Field>
        <Field label="Full name (required)">
          <input
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </Field>
        <Field label="KitNo (if known)">
          <input
            className="input"
            value={kitNo}
            onChange={(e) => setKitNo(e.target.value.replace(/\D/g, ''))}
            maxLength={6}
          />
        </Field>
        <Field label="House">
          <select
            className="input"
            value={house}
            onChange={(e) => setHouse(e.target.value)}
          >
            <option value="">Not recorded</option>
            {HOUSES.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Passed out">
          <input
            className="input"
            value={passingYear}
            onChange={(e) => setPassingYear(e.target.value.replace(/\D/g, ''))}
            placeholder="1971"
            maxLength={4}
          />
        </Field>
        <Field label="Unit or service">
          <input
            className="input"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          />
        </Field>
        <Field label="Date">
          <input
            className="input"
            type="date"
            value={dateOfDeath}
            onChange={(e) => setDateOfDeath(e.target.value)}
          />
        </Field>
        <Field label="Place">
          <input
            className="input"
            value={place}
            onChange={(e) => setPlace(e.target.value)}
          />
        </Field>
      </div>

      <div style={{ marginTop: 'var(--s-5)' }}>
        <Field label="Citation">
          <textarea
            className="input"
            style={{ height: 100, padding: 'var(--s-3) var(--s-4)', lineHeight: 1.6 }}
            value={citation}
            onChange={(e) => setCitation(e.target.value)}
          />
        </Field>
      </div>

      {error && (
        <div className="error-box" role="alert" style={{ marginTop: 'var(--s-4)' }}>
          {error}
        </div>
      )}
    </Modal>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span className="kpi-label">{label}</span>
      <div style={{ marginTop: 6 }}>{children}</div>
    </label>
  )
}
